import { useEffect, useState } from 'react';

import { Pagination } from '@material-ui/lab';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Link,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import theme from 'src/themev5';

import breakDefault from '../../../assets/imgs/articles/detail.png';
import { If } from '../../../components/If';
import { PanelLogin } from '../../../components/PanelLogin';
import { useAuthContext } from '../../../context/AuthContextProvider';
import { getOperationRelatedArticles } from '../../../services/operation';

export default function OperationRelatedArticles({ hash }: { hash: string }) {
  const { auth } = useAuthContext();
  const limit = 3;
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState(0);
  const { data, isLoading, refetch } = useQuery('relatedArticles', () =>
    getOperationRelatedArticles({ start: from, limit, operationId: hash }),
  );

  useEffect(() => {
    refetch();
  }, [hash, from]);

  const onChangePagination = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    setFrom((page - 1) * limit);
  }, [page]);

  if (isLoading)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
        <Box
          sx={{ display: 'flex', gap: 1, width: '100%', p: 2 }}
          component={Paper}
          elevation={1}>
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="50%"
            height={180}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
            }}>
            <Skeleton
              animation="wave"
              variant="text"
              width="100%"
              height={24}
            />
            <Skeleton
              animation="wave"
              variant="text"
              width="100%"
              height={24}
            />
            <Skeleton animation="wave" variant="text" width="50%" height={12} />
          </Box>
        </Box>
      </Box>
    );

  return (
    <Box p={2}>
      <If
        condition={
          !auth?.user?.subscription?.plan?.permissions
            .operacoes_artigos_relacionados
        }>
        <PanelLogin />
      </If>
      <If
        condition={
          auth?.user?.subscription?.plan?.permissions
            .operacoes_artigos_relacionados
        }>
        <Grid container spacing={1}>
          <If condition={data.data?.length > 0}>
            {data.data?.map(
              ({
                id,
                title,
                slug,
                headerImage,
                publishedAt,
                markets,
              }: {
                id: number;
                title: string;
                slug: string;
                headerImage: { url: string };
                publishedAt: string;
                markets: { id: number; name: string }[];
              }) => (
                <Card key={id} sx={{ width: '100%', marginTop: '1rem' }}>
                  <Grid item container xs={12}>
                    <Grid item xs={12} sm={6}>
                      <CardMedia
                        image={headerImage?.url ?? breakDefault}
                        title={title}
                        sx={{ width: '100%', minHeight: 180, height: '100%' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%',
                        }}>
                        <CardContent>
                          <Typography
                            variant="h5"
                            fontWeight={theme.typography.fontWeightBold}>
                            {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="p">
                            {`Publicado em ${moment(publishedAt).format('DD/MM/yyyy')}`}
                          </Typography>
                        </CardContent>
                        <CardActions
                          sx={{
                            justifyContent: 'space-between',
                            padding: '1rem',
                          }}>
                          <Box>
                            <If condition={markets?.length}>
                              {markets.map((categorie) => (
                                <Chip
                                  key={categorie.id}
                                  label={categorie.name}
                                  color="secondary"
                                  sx={{
                                    minWidth: 60,
                                    fontSize: '0.75rem',
                                    fontWeight: theme.typography.fontWeightBold,
                                    marginRight: '0.2rem',
                                    marginTop: '0.2rem',
                                  }}
                                />
                              ))}
                            </If>
                          </Box>
                          <Box
                            sx={{
                              minWidth: '70px',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'flex-end',
                            }}>
                            <Typography>
                              <Link
                                to={`/artigo/${slug}/${id}`}
                                component={RouterLink}
                                color="secondary">
                                Ver mais
                              </Link>
                            </Typography>
                          </Box>
                        </CardActions>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ),
            )}
          </If>
          <If condition={!data.data?.length}>
            <Box p={2}>
              <Typography>
                Ops… Não existe nenhum artigo relacionado a essa operação.
              </Typography>
            </Box>
          </If>
        </Grid>
        <If condition={data.data?.length > 0}>
          <Box pt={3} display="flex" justifyContent="center">
            <Pagination
              count={Math.ceil(data?.meta?.pagination?.total / limit)}
              page={page}
              onChange={onChangePagination}
              shape="rounded"
              size="large"
            />
          </Box>
        </If>
      </If>
    </Box>
  );
}
